/**
 * SuggestionField — search-UX §3.11/§3.12.
 *
 * Single-value text input + dropdown of suggestions filtered by
 * the current value.  Used in EditItemSheet (category) and
 * LocationEditSheet (location) to kill the duplicate-tag /
 * duplicate-location fragmentation that today turns the Browse
 * Tag + Location layouts into noisy duplicate-bucket lists.
 *
 * Notion's location/select picker is the precedent: pinned
 * recently-used + alphabetical full list under a single input,
 * with inline "+ Create" when the typed query has no match.
 * Zero mode-switch between pick and create.
 *
 * Pure presentation — caller owns the value + onChange.  Caller
 * also passes the suggestion source (`selectAllTags`,
 * `selectCategories`, `selectAllLocations` etc.) so this
 * component stays decoupled from any specific Redux selector.
 */

import React, { useMemo, useState } from 'react';
import {
  FlatList,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { fpColors } from '../styles/theme';

export interface Suggestion {
  /** The canonical string value the picker writes via `onChange`. */
  label: string;
  /** Optional metadata shown right-aligned (e.g. "12 items"). */
  meta?: string;
}

export interface SuggestionFieldProps {
  /** Form-state value (the canonical string). */
  value: string;
  /** Fires on every keystroke AND when the user taps a suggestion. */
  onChangeText: (next: string) => void;
  /** Field label rendered above the input. */
  label: string;
  /** Placeholder text. */
  placeholder?: string;
  /** Full suggestion pool — the component filters by `value` as the
   *  user types.  Pinned "Recent" entries sort first naturally if
   *  the caller pre-sorted; we don't impose an order. */
  suggestions: Suggestion[];
  /** Optional hint shown below the input. */
  hint?: string;
  /** Optional capitalize behavior — defaults to 'none'. */
  autoCapitalize?: 'none' | 'sentences' | 'words' | 'characters';
  /** Optional testID prefix. */
  testID?: string;
}

const MAX_DROPDOWN_ROWS = 8;

export const SuggestionField: React.FC<SuggestionFieldProps> = ({
  value,
  onChangeText,
  label,
  placeholder,
  suggestions,
  hint,
  autoCapitalize = 'none',
  testID,
}) => {
  const [focused, setFocused] = useState(false);

  const filtered = useMemo(() => {
    const q = value.trim().toLowerCase();
    const pool = suggestions.filter(
      // Hide exact-match self-row so the dropdown never tells the
      // user "pick the thing you just typed".
      (s) => s.label.toLowerCase() !== q,
    );
    if (!q) return pool.slice(0, MAX_DROPDOWN_ROWS);
    return pool
      .filter((s) => s.label.toLowerCase().includes(q))
      .slice(0, MAX_DROPDOWN_ROWS);
  }, [suggestions, value]);

  // Inline "+ Create" affordance when the typed query exists and is
  // not in the suggestion pool.  Notion picker pattern — no mode
  // switch between pick and create.
  const showCreate =
    value.trim().length > 0 &&
    !suggestions.some(
      (s) => s.label.toLowerCase() === value.trim().toLowerCase(),
    );

  return (
    <View style={styles.field}>
      <Text style={styles.fieldLabel}>{label}</Text>
      <View style={styles.inputWrap}>
        <TextInput
          value={value}
          onChangeText={onChangeText}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            // Delay so dropdown taps register first.
            setTimeout(() => setFocused(false), 120);
          }}
          placeholder={placeholder}
          placeholderTextColor={`${fpColors.outline}99`}
          autoCapitalize={autoCapitalize}
          autoCorrect={false}
          style={styles.input}
          testID={testID ? `${testID}-input` : undefined}
        />
      </View>
      {hint ? <Text style={styles.fieldHint}>{hint}</Text> : null}

      {focused && (filtered.length > 0 || showCreate) && (
        <View
          style={styles.dropdown}
          testID={testID ? `${testID}-dropdown` : undefined}
        >
          <FlatList
            data={filtered}
            keyboardShouldPersistTaps="handled"
            keyExtractor={(item) => `sug-${item.label}`}
            renderItem={({ item }) => (
              <Pressable
                onPress={() => onChangeText(item.label)}
                style={({ pressed }) => [
                  styles.row,
                  pressed && styles.rowPressed,
                ]}
                accessibilityRole="button"
                accessibilityLabel={`Use ${item.label}`}
                testID={testID ? `${testID}-row-${item.label}` : undefined}
              >
                <Text style={styles.rowLabel} numberOfLines={1}>
                  {item.label}
                </Text>
                {item.meta ? (
                  <Text style={styles.rowMeta}>{item.meta}</Text>
                ) : null}
              </Pressable>
            )}
            ListFooterComponent={
              showCreate ? (
                <Pressable
                  onPress={() => onChangeText(value.trim())}
                  style={({ pressed }) => [
                    styles.row,
                    styles.createRow,
                    pressed && styles.rowPressed,
                  ]}
                  accessibilityRole="button"
                  accessibilityLabel={`Use "${value.trim()}" as a new ${label.toLowerCase()}`}
                  testID={testID ? `${testID}-create` : undefined}
                >
                  <MaterialIcons
                    name="add"
                    size={16}
                    color={fpColors.primary}
                    style={{ marginRight: 6 }}
                  />
                  <Text style={styles.createLabel} numberOfLines={1}>
                    {`Use "${value.trim()}"`}
                  </Text>
                </Pressable>
              ) : null
            }
          />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  field: { marginBottom: 12 },
  fieldLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: fpColors.onSurfaceVariant,
    marginBottom: 4,
  },
  inputWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: fpColors.surfaceContainerLowest,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  input: {
    flex: 1,
    fontSize: 14,
    color: fpColors.onSurface,
    padding: 0,
    margin: 0,
  },
  fieldHint: {
    fontSize: 11,
    color: fpColors.onSurfaceVariant,
    marginTop: 4,
  },
  dropdown: {
    marginTop: 6,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    overflow: 'hidden',
    maxHeight: 240,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: fpColors.outlineVariant,
  },
  rowPressed: {
    backgroundColor: fpColors.surfaceContainerLow,
  },
  rowLabel: {
    flex: 1,
    fontSize: 14,
    color: fpColors.onSurface,
  },
  rowMeta: {
    fontSize: 11,
    color: fpColors.onSurfaceVariant,
  },
  createRow: {
    borderBottomWidth: 0,
  },
  createLabel: {
    flex: 1,
    fontSize: 13,
    fontWeight: '600',
    color: fpColors.primary,
  },
});

export default SuggestionField;
